import React from "react";

interface Check {
  metric: string;
  threshold: string;
}

export interface ProductGate {
  gate: string;
  icon: React.ComponentType<{ className?: string }>;
  checks: readonly Check[];
  description?: string;
}

interface Props {
  subtitle: string;
  gates: readonly ProductGate[];
  footer?: React.ReactNode;
}

const DEFAULT_FOOTER =
  "Infralyst only recommends stepping down one size at a time. If there's room to go further, it happens across multiple analysis cycles.";

export function ProductThresholds({ subtitle, gates, footer }: Props): React.ReactElement {
  const resolvedFooter = footer === undefined ? DEFAULT_FOOTER : footer;
  return (
    <section id="thresholds" className="py-16 px-4 scroll-mt-20">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-4">
          Thresholds &amp; <span className="text-primary">methodology</span>
        </h2>
        <p className="text-center text-surface-foreground/80 mb-12 max-w-2xl mx-auto">
          {subtitle}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {gates.map(({ gate, icon: Icon, checks, description }) => (
            <div
              key={gate}
              className="p-6 rounded-3xl border bg-background/50 hover:border-primary/20 hover:bg-primary/5 transition-colors"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="size-10 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-none">
                  <Icon className="size-5" />
                </div>
                <h3 className="font-bold text-lg md:text-lg">{gate}</h3>
              </div>
              {description ? (
                <p className="text-sm text-surface-foreground/70 leading-relaxed">
                  {description}
                </p>
              ) : (
                <div className="space-y-2">
                  {checks.map(({ metric, threshold }) => (
                    <div
                      key={metric}
                      className="flex items-baseline justify-between gap-4"
                    >
                      <span className="font-semibold text-sm shrink-0">
                        {metric}
                      </span>
                      <span className="text-sm text-surface-foreground/70 text-right">
                        {threshold}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {resolvedFooter ? (
          <p className="mt-8 text-center text-surface-foreground/70 text-sm max-w-2xl mx-auto">
            {resolvedFooter}
          </p>
        ) : null}
      </div>
    </section>
  );
}
